// src/components/Sections.jsx

import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import { SITE } from "../content";
import { useContent } from "../ContentContext";
import { GlowCard, Magnetic, Tilt } from "./Effects";

// ---------------------------------------------------------------------------
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-70px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 18 }}
      animate={seen ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Eyebrow({ children }) {
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brooke-red">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// The data plate riveted to a commissioned unit. Chosen over a rendered cold
// room because it is typographic and hairline only, so it holds up at every
// size, and because it leaves the gateway's cabinet as the one dimensional
// object across the estate.
// ---------------------------------------------------------------------------
const TEMP_SEQ = [-18, -17, -19, -18, -20, -18];

function SpecPlate() {
  const { plate } = useContent();
  const reduce = useReducedMotion();
  const [temp, setTemp] = useState(TEMP_SEQ[0]);

  useEffect(() => {
    if (reduce) return;
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % TEMP_SEQ.length;
      setTemp(TEMP_SEQ[i]);
    }, 2800);
    return () => clearInterval(t);
  }, [reduce]);

  return (
    <Tilt max={6}>
      <div className="bk-plate relative overflow-hidden rounded-xl border border-line shadow-[0_24px_60px_-28px_rgba(4,12,20,0.6)]">
        <div className="bk-sheen pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(100deg,transparent,rgb(var(--bk-surface)/0.8),transparent)]" />

        {/* corner fixings */}
        {["left-3 top-3", "right-3 top-3", "left-3 bottom-3", "right-3 bottom-3"].map(
          (pos) => (
            <span
              key={pos}
              className={`absolute ${pos} h-1.5 w-1.5 rounded-full bg-line shadow-[inset_0_1px_1px_rgba(0,0,0,.35)]`}
            />
          )
        )}

        <div className="relative px-5 py-6 sm:px-9 sm:py-10">
          <div className="flex items-start justify-between border-b border-line pb-5">
            <div>
              <p className="font-mono text-[8.5px] uppercase tracking-[0.24em] text-subtle">
                Brooke Refrigeration
              </p>
              <p className="mt-1 font-arch text-sm font-extrabold uppercase tracking-tight text-ink">
                Cold Storage Division
              </p>
            </div>
            <span className="flex items-center font-mono text-[8.5px] uppercase tracking-[0.18em] text-subtle">
              <i className="bk-dot mr-1.5 h-[5px] w-[5px] rounded-full bg-[#41D98A] shadow-[0_0_8px_#41D98A]" />
              Holding
            </span>
          </div>

          <div className="flex items-end justify-between py-7">
            <span className="font-mono text-[38px] font-bold leading-none tabular-nums tracking-tight text-led sm:text-[56px]">
              &minus;{Math.abs(temp)}
              <span className="ml-1 text-[0.34em] opacity-70">&deg;C</span>
            </span>
            <span className="pb-2 font-mono text-[9px] uppercase leading-relaxed tracking-[0.18em] text-subtle">
              Chamber
              <br />
              set point
            </span>
          </div>

          <dl className="border-t border-line">
            {plate.map((row) => (
              <div
                key={row.label}
                className="flex items-baseline justify-between gap-6 border-b border-line py-3"
              >
                <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-subtle">
                  {row.label}
                </dt>
                <dd className="text-right font-mono text-[11.5px] tracking-[0.04em] text-muted">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Tilt>
  );
}

// ---------------------------------------------------------------------------
export function Hero() {
  const { hero, contact } = useContent();

  return (
    <section className="bk-wash relative overflow-hidden">
      <div className="bk-vignette pointer-events-none absolute inset-0" />
      <div className="bk-panel-lines pointer-events-none absolute inset-0 opacity-40" />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 overflow-hidden">
        <i className="bk-drift absolute -left-[5%] bottom-0 block h-full w-2/3 rounded-[50%] bg-[radial-gradient(ellipse_at_50%_70%,rgb(var(--bk-led)/0.18),transparent_68%)] blur-2xl" />
        <i
          className="bk-drift absolute bottom-0 left-[38%] block h-4/5 w-2/3 rounded-[50%] bg-[radial-gradient(ellipse_at_50%_70%,rgb(var(--bk-led)/0.14),transparent_68%)] blur-2xl"
          style={{ animationDelay: "-6s" }}
        />
      </div>

      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 pb-24 pt-32 sm:px-8 sm:pb-28 sm:pt-40 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <Reveal>
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-brooke-red" />
            <Eyebrow>{SITE.division}</Eyebrow>
          </div>

          <h1 className="mt-6 max-w-[15ch] font-arch text-[clamp(34px,6.5vw,64px)] font-extrabold leading-[1.02] tracking-[-0.03em] text-ink">
            {hero.heading}
          </h1>

          <p className="mt-7 max-w-[52ch] text-[16px] leading-relaxed text-muted sm:text-[17px]">
            {hero.body}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Magnetic>
              <a
                href="#enquire"
                className="inline-flex min-h-[50px] items-center rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark"
              >
                Start an enquiry
              </a>
            </Magnetic>
            <a
              href={`tel:${contact.phone}`}
              className="inline-flex min-h-[50px] items-center rounded-full border border-line px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-muted transition hover:border-brooke-red hover:text-ink"
            >
              {contact.phoneLabel}
            </a>
          </div>

          <dl className="mt-14 flex flex-wrap gap-x-12 gap-y-6 border-t border-line pt-8">
            {hero.stats.map((s) => (
              <div key={s.label}>
                <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-subtle">
                  {s.label}
                </dt>
                <dd className="mt-1.5 font-mono text-2xl font-bold tabular-nums text-led">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal delay={0.15}>
          <SpecPlate />
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
export function Installations() {
  const { installations } = useContent();

  return (
    <section id="installations" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{installations.eyebrow}</Eyebrow>
          <h2 className="mt-3 max-w-[18ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            {installations.heading}
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-4">
            {[installations.body1, installations.body2]
              .filter(Boolean)
              .map((p, i) => (
                <p
                  key={i}
                  className="text-[15px] leading-relaxed text-muted sm:text-base"
                >
                  {p}
                </p>
              ))}
          </div>
        </Reveal>

        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3">
          {installations.items.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.08} className="h-full">
              <GlowCard className="h-full bg-bg">
                <div className="h-full p-7">
                  <span className="block h-px w-8 bg-brooke-red transition-all duration-300 group-hover/glow:w-14" />
                  <h3 className="mt-5 font-arch text-lg font-extrabold leading-snug text-ink">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-muted">{item.body}</p>
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The rule down the left of the steps fills as the section scrolls, so the
// four stages read as a sequence being worked through.
// ---------------------------------------------------------------------------
export function Process() {
  const { process } = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 60%"],
  });
  const fill = useSpring(scrollYProgress, { stiffness: 90, damping: 30 });
  const height = useTransform(fill, [0, 1], ["0%", "100%"]);

  return (
    <section id="process" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{process.eyebrow}</Eyebrow>
          <h2 className="mt-3 max-w-[16ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            {process.heading}
          </h2>
        </Reveal>

        <div ref={ref} className="relative mt-14 sm:pl-6">
          {/* the run of the job */}
          <div className="absolute left-0 top-0 hidden h-full w-px bg-line sm:block">
            <motion.div
              className="w-px bg-gradient-to-b from-brooke-red to-led"
              style={{ height }}
            />
          </div>

          <ol>
            {process.steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <li className="group grid gap-4 border-t border-line py-8 sm:grid-cols-[80px_1fr] sm:gap-10">
                  <span className="border-l-2 border-brooke-red pl-3 font-mono text-[13px] font-bold tracking-[0.1em] text-subtle transition-colors group-hover:text-brooke-red">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-arch text-xl font-extrabold text-ink">{s.title}</h3>
                    <p className="mt-2.5 max-w-[64ch] text-[14.5px] leading-relaxed text-muted">
                      {s.body}
                    </p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
export function Why() {
  const { why } = useContent();

  return (
    <section className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
            <div>
              <Eyebrow>{why.eyebrow}</Eyebrow>
              <h2 className="mt-3 max-w-[14ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
                {why.heading}
              </h2>
            </div>
            <p className="max-w-[58ch] self-end text-[15px] leading-relaxed text-muted sm:text-base">
              {why.body}
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-3">
          {why.points.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="h-full">
              <GlowCard className="h-full rounded-2xl border border-line bg-surface2">
                <div className="h-full p-7">
                  <span className="font-mono text-[11px] font-bold tracking-[0.1em] text-brooke-red">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-arch text-lg font-extrabold leading-snug text-ink">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-muted">{p.body}</p>
                </div>
              </GlowCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
export function Sectors() {
  const { sectors, areas, contact } = useContent();

  return (
    <section id="sectors" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <Eyebrow>{sectors.eyebrow}</Eyebrow>
          <h2 className="mt-3 font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            {sectors.heading}
          </h2>
          <ul className="mt-8 border-t border-line">
            {sectors.list.map((s) => (
              <li
                key={s}
                className="group flex items-center gap-3 border-b border-line py-3 font-mono text-[12px] tracking-[0.04em] text-muted transition-colors hover:text-ink"
              >
                <span className="h-px w-3.5 bg-led/70 transition-all duration-300 group-hover:w-7" />
                {s}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1}>
          <Eyebrow>Coverage</Eyebrow>
          <h2 className="mt-3 font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
            Where we work
          </h2>
          <p className="mt-6 max-w-[46ch] text-[15px] leading-relaxed text-muted">
            Installation work across Gauteng from the workshop in{" "}
            {contact.locality}, with projects further afield by arrangement.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {areas.map((a) => (
              <li
                key={a}
                className="rounded-full border border-line px-4 py-2 font-mono text-[10px] uppercase tracking-[0.1em] text-muted transition hover:border-brooke-red hover:text-ink"
              >
                {a}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}